/**
 * Where a request stands. NUMERIC on the wire — no string enum converter is configured — so this
 * is an `as const` object of the server's own numbers, never a string union.
 */
export const ATTRITION_STATUS = {
  pendingDocuments: 0,
  forReview: 1,
  completed: 2,
  cancelled: 3,
} as const;

export type AttritionRequestStatus = (typeof ATTRITION_STATUS)[keyof typeof ATTRITION_STATUS];

/** Where one document stands with its reviewer. Numeric, mirroring the server. */
export const ATTRITION_REVIEW = {
  notSubmitted: 0,
  forReview: 1,
  approved: 2,
  returned: 3,
} as const;

export type AttritionDocumentReviewStatus = (typeof ATTRITION_REVIEW)[keyof typeof ATTRITION_REVIEW];

/** One row of the register. It carries the employee's personId and NO name. */
export interface AttritionRequestListItem {
  id: string;
  requestNumber: string;
  status: AttritionRequestStatus;
  statusLabel: string;
  personnelId: string;
  /** Omitted from the wire where the employee is above the reader's clearance — read with `!= null`. */
  personId?: string | null;
  employeeNumber?: string | null;
  attritionCategoryId: string;
  categoryName: string;
  effectiveDate?: string | null;
  documentCount: number;
  approvedCount: number;
  /** Lines waiting on the CALLER's own review. */
  awaitingMeCount: number;

  isActive: boolean;
  createdAt: string;
  createdByName?: string | null;
  updatedAt?: string | null;
  updatedByName?: string | null;
}

export interface AttritionRequestDocument {
  id: string;
  attritionCategoryDocumentId: string;
  name: string;
  description?: string | null;
  reviewerUserId: string;
  reviewerName?: string | null;
  /** Whether the CALLER is this line's reviewer. */
  isMine: boolean;
  fileName?: string | null;
  /** The stored file's media type, VERIFIED by magic bytes on write — what the preview keys on. */
  contentType?: string | null;
  sizeBytes?: number | null;
  submittedAt?: string | null;
  submittedByName?: string | null;
  reviewStatus: AttritionDocumentReviewStatus;
  reviewStatusLabel: string;
  reviewRemarks?: string | null;
  reviewedAt?: string | null;
  reviewedByName?: string | null;
}

export interface AttritionRequestDetail {
  id: string;
  requestNumber: string;
  status: AttritionRequestStatus;
  statusLabel: string;
  personnelId: string;
  personId?: string | null;
  employeeNumber?: string | null;
  attritionCategoryId: string;
  categoryName: string;
  effectiveDate?: string | null;
  remarks?: string | null;
  /** The checklist as it was asked of this request. */
  documents: AttritionRequestDocument[];
  /** Live and still open — what the form, the upload and the cancel key on. */
  isOpen: boolean;
  cancelReason?: string | null;
  cancelledAt?: string | null;
  cancelledByName?: string | null;

  isActive: boolean;
  createdAt: string;
  createdByName?: string | null;
  updatedAt?: string | null;
  updatedByName?: string | null;
}

export interface AttritionRequestListQuery {
  search?: string;
  page?: number;
  pageSize?: number;
  sort?: string;
  status?: string;
  stage?: string;
  category?: string;
  awaitingMe?: boolean;
}

/** Create and edit. The number, the status, the checklist and every stamp are the server's. */
export interface AttritionRequestRequest {
  personnelId: string | null;
  attritionCategoryId: string | null;
  effectiveDate: string | null;
  remarks: string | null;
}

/** The module's one list of what its writes touch — read by every History Logs surface. */
export const ATTRITION_REQUEST_AUDIT_ENTITY_TYPES = ['AttritionRequest', 'AttritionRequestDocument'];
